import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { DashboardLayout } from "../components/DashboardLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FileText, Filter, Download, X, Mail, PlusCircle, CalendarDays, Clock, ChevronLeft, ChevronRight, BellRing, ChevronDown, FileSpreadsheet } from "lucide-react";

const FILTERABLE_STATUSES = ["to_invoice", "pending_payment", "partial_payment", "overdue", "overpaid", "paid"];

// Attribution date used for date-range filtering (matches backend: invoice_date > period_start > created_at)
const getAttributionDate = (inv) => {
  const raw = inv.invoice_date || inv.period_start || inv.created_at;
  if (!raw) return null;
  const d = new Date(String(raw).slice(0, 10) + "T00:00:00");
  return isNaN(d.getTime()) ? null : d;
};

const statusConfig = {
  to_invoice: { label: "To Invoice", color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200" },
  pending_payment: { label: "Pending Payment", color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200" },
  paid: { label: "Paid", color: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" },
  partial_payment: { label: "Partial Payment", color: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200" },
  overpaid: { label: "Overpaid", color: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200" },
  overdue: { label: "Overdue", color: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200" },
};

// Each new line item carries a stable _key so React can track edits/deletes correctly
const newLineItem = () => ({
  _key: (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `li-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  description: "",
  quantity: 1,
  unit_price: 0,
  amount: 0,
});

export default function InvoicesPage() {
  const { token } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [statusFilters, setStatusFilters] = useState([]); // [] = all statuses
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(false);
  const [selectedClientTab, setSelectedClientTab] = useState("all"); // "all" or client_id
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailingInvoice, setEmailingInvoice] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailFormData, setEmailFormData] = useState({
    recipient_email: "",
    subject: "",
    message: "",
    cc: "",
    due_date: "",
    test_send: false,
    table_rows: []
  });
  const [aiPrompt, setAiPrompt] = useState("");
  const [generatingEmail, setGeneratingEmail] = useState(false);
  // Reminder dialog state
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [remindingInvoice, setRemindingInvoice] = useState(null);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [reminderFormData, setReminderFormData] = useState({
    recipient_email: "",
    cc: "",
    custom_message: "",
    include_pdf: true,
  });
  const [formData, setFormData] = useState({
    client_id: "",
    invoice_number: "",
    amount: "",
    tax: "",
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: "",
    period_start: "",
    period_end: "",
    status: "to_invoice",
    notes: "",
    sent_date: "",
    payment_date: "",
    paid_amount: "",
    line_items: [newLineItem()]
  });

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // ---- Timesheet helpers ----
  const getMondayOf = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr + "T00:00:00");
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    return d.toISOString().split('T')[0];
  };
  const getSundayOf = (mondayStr) => {
    if (!mondayStr) return "";
    const d = new Date(mondayStr + "T00:00:00");
    d.setDate(d.getDate() + 6);
    return d.toISOString().split('T')[0];
  };
  const formatWeekLabel = (mondayStr) => {
    if (!mondayStr) return "";
    const d = new Date(mondayStr + "T00:00:00");
    const sun = new Date(d); sun.setDate(d.getDate() + 6);
    const fmt = (dt) => dt.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
    return `${fmt(d)} – ${fmt(sun)}`;
  };
  const shiftWeek = (mondayStr, direction) => {
    if (!mondayStr) return "";
    const d = new Date(mondayStr + "T00:00:00");
    d.setDate(d.getDate() + direction * 7);
    return d.toISOString().split('T')[0];
  };

  // Timesheet state for email dialog
  const [timesheetWeekStart, setTimesheetWeekStart] = useState("");
  const [timesheetRows, setTimesheetRows] = useState([]);
  const [loadingTimesheet, setLoadingTimesheet] = useState(false);
  const [includeTimesheet, setIncludeTimesheet] = useState(false);

  const generateEmailWithAI = async () => {
    if (!aiPrompt.trim()) return;
    setGeneratingEmail(true);
    try {
      const inv = emailingInvoice;
      const res = await axios.post(`${API}/ai/generate-email`, {
        prompt: aiPrompt,
        invoice_number: inv?.invoice_number,
        client_name: inv?.client_name,
        amount: inv?.total,
        due_date: inv?.due_date
      }, { headers });
      setEmailFormData(prev => ({ ...prev, message: res.data.content }));
    } catch {
      toast.error("AI generation failed");
    } finally {
      setGeneratingEmail(false);
    }
  };

  const fetchTimesheetForWeek = async (weekStart, clientId) => {
    if (!weekStart || !clientId) return;
    setLoadingTimesheet(true);
    try {
      const weekEnd = getSundayOf(weekStart);
      const res = await axios.get(`${API}/timesheets`, {
        headers,
        params: { start_date: weekStart, end_date: weekEnd }
      });
      const clientRows = res.data.filter(ts => ts.client_id === clientId);
      setTimesheetRows(clientRows);
      setIncludeTimesheet(clientRows.length > 0);
    } catch {
      toast.error("Failed to load timesheet");
    } finally {
      setLoadingTimesheet(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [invoicesRes, clientsRes] = await Promise.all([
        axios.get(`${API}/invoices`, { headers }),
        axios.get(`${API}/clients`, { headers })
      ]);
      setInvoices(invoicesRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const generateInvoiceNumber = () => {
    const prefix = "MLC";
    const num = String(Math.floor(Math.random() * 9000) + 1000).padStart(4, '0');
    return `${prefix}${num}`;
  };

  // Filtered invoices for display: apply client tab, multi-status, and date range
  const filteredInvoices = invoices.filter(inv => {
    const clientMatch = selectedClientTab === "all" || inv.client_id === selectedClientTab;
    const statusMatch = statusFilters.length === 0 || statusFilters.includes(inv.status);
    let dateMatch = true;
    if (dateFrom || dateTo) {
      const d = getAttributionDate(inv);
      if (!d) {
        dateMatch = false;
      } else {
        if (dateFrom && d < new Date(dateFrom + "T00:00:00")) dateMatch = false;
        if (dateTo && d > new Date(dateTo + "T00:00:00")) dateMatch = false;
      }
    }
    return clientMatch && statusMatch && dateMatch;
  });

  const toggleStatusFilter = (status) => {
    setStatusFilters(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  const clearFilters = () => {
    setStatusFilters([]);
    setDateFrom("");
    setDateTo("");
  };

  const exportStatement = async () => {
    setExporting(true);
    try {
      const params = {};
      if (statusFilters.length > 0) params.statuses = statusFilters.join(",");
      if (selectedClientTab !== "all") params.client_id = selectedClientTab;
      if (dateFrom) params.start_date = dateFrom;
      if (dateTo) params.end_date = dateTo;
      const response = await axios.get(`${API}/export/invoices/statement`, {
        headers, params, responseType: 'blob'
      });
      const clientName = selectedClientTab !== "all"
        ? (clients.find(c => c.id === selectedClientTab)?.name || "client").replace(/\s+/g, "_")
        : "All_Clients";
      const statusTag = statusFilters.length > 0 ? statusFilters.join("_") : "all";
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `statement_${clientName}_${statusTag}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Statement exported (${filteredInvoices.length} invoices)`);
    } catch (error) {
      toast.error("Failed to export statement");
    } finally {
      setExporting(false);
    }
  };

  // Pre-fill new invoice form, optionally with a specific client
  const openNewInvoiceDialog = (prefillClientId = null) => {
    const cid = prefillClientId || (selectedClientTab !== "all" ? selectedClientTab : "");
    setEditingInvoice(null);
    setFormData({
      client_id: cid,
      invoice_number: generateInvoiceNumber(),
      amount: "",
      tax: "",
      invoice_date: new Date().toISOString().split('T')[0],
      due_date: "",
      period_start: "",
      period_end: "",
      status: "to_invoice",
      notes: "",
      sent_date: "",
      payment_date: "",
      paid_amount: "",
      line_items: [newLineItem()]
    });
    setDialogOpen(true);
  };

  const calculateTotals = (lineItems) => {
    const subtotal = lineItems.reduce((sum, item) => sum + (item.amount || 0), 0);
    const tax = subtotal * 0.1; // 10% GST
    return { subtotal, tax };
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...formData.line_items];
    newItems[index][field] = value;
    
    // Recalculate amount
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].amount = (parseFloat(newItems[index].quantity) || 0) * (parseFloat(newItems[index].unit_price) || 0);
    }
    
    // Update totals
    const { subtotal, tax } = calculateTotals(newItems);
    setFormData({ 
      ...formData, 
      line_items: newItems,
      amount: subtotal.toString(),
      tax: tax.toString()
    });
  };

  const addLineItem = () => {
    setFormData({
      ...formData,
      line_items: [...formData.line_items, newLineItem()]
    });
  };

  const removeLineItem = (index) => {
    if (formData.line_items.length <= 1) return;
    const newItems = formData.line_items.filter((_, i) => i !== index);
    const { subtotal, tax } = calculateTotals(newItems);
    setFormData({
      ...formData,
      line_items: newItems,
      amount: subtotal.toString(),
      tax: tax.toString()
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const lineItems = formData.line_items.map(item => ({
        description: item.description,
        quantity: parseFloat(item.quantity) || 1,
        unit_price: parseFloat(item.unit_price) || 0,
        amount: parseFloat(item.amount) || 0
      }));

      const data = {
        client_id: formData.client_id,
        invoice_number: formData.invoice_number,
        amount: parseFloat(formData.amount) || 0,
        tax: parseFloat(formData.tax) || 0,
        invoice_date: formData.invoice_date || null,
        due_date: formData.due_date || null,  // null = backend auto-computes from invoice_date + payment_terms
        period_start: formData.period_start,
        period_end: formData.period_end,
        status: formData.status,
        notes: formData.notes,
        line_items: lineItems,
        sent_date: formData.sent_date || null,
        payment_date: formData.payment_date || null,
      };
      // Include paid_amount only for partial/overpaid
      if (["partial_payment", "overpaid"].includes(formData.status)) {
        data.paid_amount = parseFloat(formData.paid_amount) || 0;
      }
      
      if (editingInvoice) {
        await axios.put(`${API}/invoices/${editingInvoice.id}`, data, { headers });
        toast.success("Invoice updated successfully");
      } else {
        await axios.post(`${API}/invoices`, data, { headers });
        toast.success("Invoice created successfully");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save invoice");
    }
  };

  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
    const lineItems = invoice.line_items && invoice.line_items.length > 0 
      ? invoice.line_items 
      : [{ description: invoice.notes || '', quantity: 1, unit_price: invoice.amount, amount: invoice.amount }];
    
    setFormData({
      client_id: invoice.client_id,
      invoice_number: invoice.invoice_number,
      amount: invoice.amount?.toString() || "",
      tax: invoice.tax?.toString() || "",
      invoice_date: invoice.invoice_date || invoice.created_at?.split('T')[0] || new Date().toISOString().split('T')[0],
      due_date: invoice.due_date || "",
      period_start: invoice.period_start,
      period_end: invoice.period_end,
      status: invoice.status,
      notes: invoice.notes || "",
      sent_date: invoice.sent_date || "",
      payment_date: invoice.payment_date || "",
      paid_amount: invoice.paid_amount?.toString() || "",
      line_items: lineItems
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this invoice?")) return;
    
    try {
      await axios.delete(`${API}/invoices/${id}`, { headers });
      toast.success("Invoice deleted successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete invoice");
    }
  };

  const downloadPdf = async (invoiceId, invoiceNumber) => {
    setDownloadingPdf(invoiceId);
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`, {
        headers,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Invoice PDF downloaded");
    } catch (error) {
      toast.error("Failed to download PDF");
    } finally {
      setDownloadingPdf(null);
    }
  };

  const openEmailDialog = (invoice) => {
    setEmailingInvoice(invoice);
    const client = clients.find(c => c.id === invoice.client_id);
    const ccEmails = client?.additional_emails?.length > 0 ? client.additional_emails.join(', ') : "";
    setEmailFormData({
      recipient_email: client?.contact_email || "",
      subject: `Invoice ${invoice.invoice_number} from Malla & Co Pty Ltd`,
      message: "",
      cc: ccEmails,
      due_date: invoice.due_date || "",
      test_send: false,
      table_rows: []
    });
    const monday = getMondayOf(invoice.period_start);
    setTimesheetWeekStart(monday);
    setTimesheetRows([]);
    setIncludeTimesheet(false);
    setAiPrompt("");
    setEmailDialogOpen(true);
    if (monday) fetchTimesheetForWeek(monday, invoice.client_id);
  };

  const addTableRow = () => setEmailFormData(f => ({
    ...f,
    table_rows: [...f.table_rows, {
      _key: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `tr-${Date.now()}-${Math.random()}`,
      label: "", value: "",
    }]
  }));
  const removeTableRow = (idx) => setEmailFormData(f => ({ ...f, table_rows: f.table_rows.filter((_, i) => i !== idx) }));
  const updateTableRow = (idx, field, val) => setEmailFormData(f => ({
    ...f,
    table_rows: f.table_rows.map((r, i) => i === idx ? { ...r, [field]: val } : r)
  }));

  const sendInvoiceEmail = async (e) => {
    e.preventDefault();
    if (!emailingInvoice) return;
    
    setSendingEmail(true);
    try {
      const payload = {
        ...emailFormData,
        due_date: emailFormData.due_date || undefined,
        test_send: !!emailFormData.test_send,
        cc: emailFormData.cc ? emailFormData.cc.split(',').map(e => e.trim()).filter(Boolean) : undefined,
        timesheet_rows: (includeTimesheet && timesheetRows.length > 0) ? timesheetRows.map(r => ({
          date: r.date,
          project_name: r.project_name || "",
          description: r.description || "",
          hours: r.hours
        })) : undefined
      };
      const { data: emailResult } = await axios.post(`${API}/invoices/${emailingInvoice.id}/email`, payload, { headers });
      if (emailResult?.invoice_status === "pending_payment") {
        toast.success(`Invoice sent · Status → Pending Payment`);
      } else if (emailFormData.test_send) {
        toast.success(`Test send delivered · Status unchanged`);
      } else {
        toast.success(`Invoice sent to ${emailFormData.recipient_email}`);
      }
      setEmailDialogOpen(false);
      setEmailingInvoice(null);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to send email";
      if (message.includes("not configured")) {
        toast.error("Email service not configured. Please add RESEND_API_KEY to .env");
      } else {
        toast.error(message);
      }
    } finally {
      setSendingEmail(false);
    }
  };

  const openReminderDialog = (invoice) => {
    const client = clients.find(c => c.id === invoice.client_id);
    const ccEmails = client?.additional_emails?.length > 0 ? client.additional_emails.join(', ') : "";
    setRemindingInvoice(invoice);
    setReminderFormData({
      recipient_email: client?.contact_email || "",
      cc: ccEmails,
      custom_message: "",
      include_pdf: true,
    });
    setReminderDialogOpen(true);
  };

  const sendReminder = async (e) => {
    e.preventDefault();
    if (!remindingInvoice) return;
    setSendingReminder(true);
    try {
      const payload = {
        recipient_email: reminderFormData.recipient_email,
        cc: reminderFormData.cc ? reminderFormData.cc.split(',').map(x => x.trim()).filter(Boolean) : undefined,
        custom_message: reminderFormData.custom_message || undefined,
        include_pdf: !!reminderFormData.include_pdf,
      };
      const { data } = await axios.post(`${API}/invoices/${remindingInvoice.id}/reminder`, payload, { headers });
      toast.success(
        data.is_overdue
          ? `Overdue reminder #${data.reminder_count} sent (${data.days_overdue} days overdue)`
          : `Reminder #${data.reminder_count} sent`
      );
      setReminderDialogOpen(false);
      setRemindingInvoice(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reminder");
    } finally {
      setSendingReminder(false);
    }
  };

  const resetForm = () => {
    setEditingInvoice(null);
    setFormData({
      client_id: "",
      invoice_number: generateInvoiceNumber(),
      amount: "",
      tax: "",
      period_start: "",
      period_end: "",
      status: "to_invoice",
      notes: "",
      sent_date: "",
      payment_date: "",
      paid_amount: "",
      line_items: [newLineItem()]
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', { 
      style: 'currency', 
      currency: 'AUD',
      minimumFractionDigits: 2
    }).format(value);
  };

  // Calculate summary — scoped to active client tab
  const scopedInvoices = selectedClientTab === "all" ? invoices : invoices.filter(i => i.client_id === selectedClientTab);
  const summary = {
    total:     scopedInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0),
    paid:      scopedInvoices.filter(i => ["paid", "overpaid"].includes(i.status)).reduce((sum, inv) => sum + (inv.paid_amount ?? inv.total ?? 0), 0)
             + scopedInvoices.filter(i => i.status === "partial_payment").reduce((sum, inv) => sum + (inv.paid_amount || 0), 0),
    pending:   scopedInvoices.filter(i => i.status === "pending_payment").reduce((sum, inv) => sum + (inv.total || 0), 0)
             + scopedInvoices.filter(i => i.status === "partial_payment").reduce((sum, inv) => sum + (inv.outstanding_balance || 0), 0),
    toInvoice: scopedInvoices.filter(i => i.status === "to_invoice").reduce((sum, inv) => sum + (inv.total || 0), 0),
  };

  return (
    <DashboardLayout title="Invoices">
      <div className="space-y-6" data-testid="invoices-page">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="card-hover">
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">
                {selectedClientTab !== "all"
                  ? `${clients.find(c => c.id === selectedClientTab)?.name} — Total`
                  : "Total Invoiced"}
              </p>
              <p className="stat-value text-lg mt-1">{formatCurrency(summary.total)}</p>
            </CardContent>
          </Card>
          <Card className="card-hover">
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">Paid</p>
              <p className="stat-value text-lg mt-1 positive-value">{formatCurrency(summary.paid)}</p>
            </CardContent>
          </Card>
          <Card className="card-hover">
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">Pending Payment</p>
              <p className="stat-value text-lg mt-1 text-yellow-600">{formatCurrency(summary.pending)}</p>
            </CardContent>
          </Card>
          <Card className="card-hover">
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">To Invoice</p>
              <p className="stat-value text-lg mt-1 text-blue-600">{formatCurrency(summary.toInvoice)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Client Tabs */}
        <div className="flex gap-1 flex-wrap" style={{ borderBottom: "1px solid hsl(var(--border))", paddingBottom: 0 }}>
          <button
            onClick={() => setSelectedClientTab("all")}
            data-testid="client-tab-all"
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
              selectedClientTab === "all"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            All Invoices
            <span className="ml-2 text-xs bg-muted rounded-full px-2 py-0.5">{invoices.length}</span>
          </button>
          {clients.map(client => {
            const count = invoices.filter(i => i.client_id === client.id).length;
            return (
              <button
                key={client.id}
                onClick={() => setSelectedClientTab(client.id)}
                data-testid={`client-tab-${client.id}`}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px whitespace-nowrap ${
                  selectedClientTab === client.id
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {client.name}
                <span className="ml-2 text-xs bg-muted rounded-full px-2 py-0.5">{count}</span>
              </button>
            );
          })}
        </div>

        {/* Header: filters + export */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Multi-status filter */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="gap-2" data-testid="status-filter-trigger">
                  <Filter className="w-4 h-4 text-muted-foreground" />
                  {statusFilters.length === 0
                    ? "All Statuses"
                    : `${statusFilters.length} status${statusFilters.length > 1 ? "es" : ""}`}
                  <ChevronDown className="w-4 h-4 opacity-60" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-2" align="start">
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Filter by status
                </div>
                <div className="space-y-0.5">
                  {FILTERABLE_STATUSES.map(st => (
                    <label
                      key={st}
                      htmlFor={`status-cb-${st}`}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-accent cursor-pointer text-sm"
                    >
                      <Checkbox
                        id={`status-cb-${st}`}
                        checked={statusFilters.includes(st)}
                        onCheckedChange={() => toggleStatusFilter(st)}
                        data-testid={`status-filter-${st}`}
                      />
                      <span className="flex-1">{statusConfig[st]?.label || st}</span>
                    </label>
                  ))}
                </div>
                {statusFilters.length > 0 && (
                  <button
                    onClick={() => setStatusFilters([])}
                    className="mt-2 w-full text-xs text-primary underline text-left px-2"
                    data-testid="clear-status-filters"
                  >
                    Clear statuses
                  </button>
                )}
              </PopoverContent>
            </Popover>

            {/* Date range */}
            <div className="flex items-center gap-1.5">
              <CalendarDays className="w-4 h-4 text-muted-foreground" />
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-[150px] h-9"
                data-testid="date-from-input"
                aria-label="From date"
              />
              <span className="text-muted-foreground text-sm">→</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-[150px] h-9"
                data-testid="date-to-input"
                aria-label="To date"
              />
            </div>

            {(statusFilters.length > 0 || dateFrom || dateTo) && (
              <button
                onClick={clearFilters}
                className="text-xs text-primary underline"
                data-testid="clear-all-filters"
              >
                Clear all
              </button>
            )}

            {selectedClientTab !== "all" && (
              <span className="text-sm text-muted-foreground">
                — {clients.find(c => c.id === selectedClientTab)?.name}
                <button onClick={() => setSelectedClientTab("all")} className="ml-2 text-xs text-primary underline">
                  clear
                </button>
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="gap-2"
              onClick={exportStatement}
              disabled={exporting || filteredInvoices.length === 0}
              title="Export the current filtered view to Excel (shareable statement)"
              data-testid="export-statement-btn"
            >
              <FileSpreadsheet className={`w-4 h-4 ${exporting ? "animate-pulse" : ""}`} />
              {exporting ? "Exporting..." : "Export View"}
            </Button>

          <Dialog open={dialogOpen} onOpenChange={(open) => {
            if (!open) { setDialogOpen(false); resetForm(); }
          }}>
            <Button
              className="btn-hover"
              data-testid="add-invoice-btn"
              onClick={() => openNewInvoiceDialog()}
            >
              <Plus className="w-4 h-4 mr-2" />
              {selectedClientTab !== "all"
                ? `New Invoice for ${clients.find(c => c.id === selectedClientTab)?.name || "Client"}`
                : "Create Invoice"}
            </Button>
            <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingInvoice ? "Edit Invoice" : "Create New Invoice"}</DialogTitle>
                <DialogDescription>
                  {editingInvoice ? "Update invoice information" : "Enter the invoice details below"}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="invoice_number">Invoice Number *</Label>
                    <Input
                      id="invoice_number"
                      value={formData.invoice_number}
                      onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                      placeholder="MLC0001"
                      required
                      data-testid="invoice-number-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="client">Client *</Label>
                    <Select
                      value={formData.client_id}
                      onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                      required
                    >
                      <SelectTrigger data-testid="invoice-client-select">
                        <SelectValue placeholder="Select client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="invoice_date">Invoice Date *</Label>
                    <Input
                      id="invoice_date"
                      type="date"
                      value={formData.invoice_date}
                      onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                      required
                      data-testid="invoice-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="due_date">Due Date</Label>
                    <Input
                      id="due_date"
                      type="date"
                      value={formData.due_date || ""}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                      data-testid="invoice-due-date-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Auto-set from client&apos;s payment terms if left blank.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="period_start">Period Start *</Label>
                    <Input
                      id="period_start"
                      type="date"
                      value={formData.period_start}
                      onChange={(e) => setFormData({ ...formData, period_start: e.target.value })}
                      required
                      data-testid="invoice-period-start-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="period_end">Period End *</Label>
                    <Input
                      id="period_end"
                      type="date"
                      value={formData.period_end}
                      onChange={(e) => setFormData({ ...formData, period_end: e.target.value })}
                      required
                      data-testid="invoice-period-end-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value) => setFormData({ ...formData, status: value })}
                    >
                      <SelectTrigger data-testid="invoice-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="to_invoice">To Invoice</SelectItem>
                        <SelectItem value="pending_payment">Pending Payment</SelectItem>
                        <SelectItem value="paid">Paid (Full)</SelectItem>
                        <SelectItem value="partial_payment">Partial Payment</SelectItem>
                        <SelectItem value="overpaid">Overpaid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Conditional Paid Amount field */}
                {["partial_payment", "overpaid"].includes(formData.status) && (
                  <div className="space-y-2 p-3 bg-orange-50 dark:bg-orange-950 border border-orange-200 dark:border-orange-800 rounded-lg">
                    <Label htmlFor="paid_amount" className="text-orange-800 dark:text-orange-200 font-medium">
                      {formData.status === "overpaid" ? "Amount Received (inc. overpayment)" : "Amount Paid So Far"}
                    </Label>
                    <Input
                      id="paid_amount"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={formData.paid_amount}
                      onChange={(e) => setFormData({ ...formData, paid_amount: e.target.value })}
                      data-testid="paid-amount-input"
                    />
                    {formData.amount && formData.paid_amount && (
                      <p className="text-xs text-orange-700 dark:text-orange-300">
                        Outstanding: AUD {Math.max(0, (parseFloat(formData.amount) + parseFloat(formData.tax || 0)) - parseFloat(formData.paid_amount)).toFixed(2)}
                      </p>
                    )}
                  </div>
                )}

                {/* Sent Date and Payment Date */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="sent_date">Invoice Sent Date</Label>
                    <Input
                      id="sent_date"
                      type="date"
                      value={formData.sent_date}
                      onChange={(e) => setFormData({ ...formData, sent_date: e.target.value })}
                      data-testid="invoice-sent-date-input"
                    />
                    <p className="text-xs text-muted-foreground">Date invoice was sent to client</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="payment_date">Payment Received Date</Label>
                    <Input
                      id="payment_date"
                      type="date"
                      value={formData.payment_date}
                      onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                      disabled={!["paid", "partial_payment", "overpaid"].includes(formData.status)}
                      data-testid="invoice-payment-date-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      {["paid","partial_payment","overpaid"].includes(formData.status) ? "Date payment was received" : "Available when status is Paid / Partial / Overpaid"}
                    </p>
                  </div>
                </div>

                {/* Line Items */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label>Line Items</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
                      <Plus className="w-3 h-3 mr-1" /> Add Item
                    </Button>
                  </div>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-2 text-xs font-medium text-muted-foreground">Description</th>
                          <th className="text-right p-2 text-xs font-medium text-muted-foreground w-20">Qty</th>
                          <th className="text-right p-2 text-xs font-medium text-muted-foreground w-28">Unit Price</th>
                          <th className="text-right p-2 text-xs font-medium text-muted-foreground w-28">Amount</th>
                          <th className="w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.line_items.map((item, index) => (
                          <tr key={item._key ?? `li-${index}`} className="border-t">
                            <td className="p-2">
                              <Input
                                value={item.description}
                                onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                                placeholder="e.g., Mano - Contractor (9th Feb - 13th Feb)"
                                className="h-8"
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                type="number"
                                step="0.5"
                                value={item.quantity}
                                onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                                className="h-8 text-right data-cell"
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                type="number"
                                step="0.01"
                                value={item.unit_price}
                                onChange={(e) => updateLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                                className="h-8 text-right data-cell"
                              />
                            </td>
                            <td className="p-2 text-right data-cell">
                              ${(item.amount || 0).toFixed(2)}
                            </td>
                            <td className="p-2">
                              {formData.line_items.length > 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={() => removeLineItem(index)}
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Totals */}
                <div className="flex justify-end">
                  <div className="w-64 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal:</span>
                      <span className="data-cell">{formatCurrency(parseFloat(formData.amount) || 0)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>GST (10%):</span>
                      <span className="data-cell">{formatCurrency(parseFloat(formData.tax) || 0)}</span>
                    </div>
                    <div className="flex justify-between font-semibold border-t pt-2">
                      <span>Total:</span>
                      <span className="data-cell text-primary">
                        {formatCurrency((parseFloat(formData.amount) || 0) + (parseFloat(formData.tax) || 0))}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes (appears on invoice)</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes..."
                    rows={2}
                    data-testid="invoice-notes-input"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="save-invoice-btn">
                    {editingInvoice ? "Update" : "Create"} Invoice
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Invoices Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-muted-foreground">Loading invoices...</div>
              </div>
            ) : filteredInvoices.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <FileText className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">
                  {selectedClientTab !== "all"
                    ? `No invoices for ${clients.find(c => c.id === selectedClientTab)?.name}`
                    : "No invoices yet"}
                </h3>
                <p className="text-muted-foreground mt-1">
                  {selectedClientTab !== "all"
                    ? "Click \"New Invoice\" to create one for this client"
                    : "Create your first invoice to get started"}
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="table-header">Invoice #</TableHead>
                    <TableHead className="table-header">Client</TableHead>
                    <TableHead className="table-header">Period</TableHead>
                    <TableHead className="table-header">Status</TableHead>
                    <TableHead className="table-header text-right">Ex-GST</TableHead>
                    <TableHead className="table-header text-right">GST</TableHead>
                    <TableHead className="table-header text-right">Total (inc GST)</TableHead>
                    <TableHead className="table-header text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInvoices.map((invoice) => (
                    <TableRow key={invoice.id} className="hover:bg-accent/50 transition-colors">
                      <TableCell className="font-medium data-cell">{invoice.invoice_number}</TableCell>
                      <TableCell>{invoice.client_name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {invoice.period_start} → {invoice.period_end}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-0.5">
                          <Badge variant="secondary" className={statusConfig[invoice.status]?.color}>
                            {statusConfig[invoice.status]?.label}
                          </Badge>
                          {["partial_payment", "overpaid"].includes(invoice.status) && invoice.paid_amount != null && (
                            <span className="text-xs text-muted-foreground">
                              Paid: {formatCurrency(invoice.paid_amount)}
                              {invoice.outstanding_balance > 0 && ` · Owed: ${formatCurrency(invoice.outstanding_balance)}`}
                              {invoice.outstanding_balance < 0 && ` · Credit: ${formatCurrency(Math.abs(invoice.outstanding_balance))}`}
                            </span>
                          )}
                          {(invoice.reminder_count || 0) > 0 && (
                            <span className="text-xs text-orange-700 flex items-center gap-1" data-testid={`reminder-badge-${invoice.id}`}>
                              <BellRing className="w-3 h-3" />
                              Reminded {invoice.reminder_count}x
                              {invoice.last_reminder_sent_date && ` · ${invoice.last_reminder_sent_date}`}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right data-cell">{formatCurrency(invoice.amount)}</TableCell>
                      <TableCell className="text-right data-cell text-muted-foreground">
                        {formatCurrency(invoice.tax)}
                      </TableCell>
                      <TableCell className="text-right data-cell font-medium">
                        {formatCurrency(invoice.total)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => downloadPdf(invoice.id, invoice.invoice_number)}
                            disabled={downloadingPdf === invoice.id}
                            title="Download PDF"
                            data-testid={`download-invoice-${invoice.id}`}
                          >
                            <Download className={`w-4 h-4 ${downloadingPdf === invoice.id ? 'animate-pulse' : ''}`} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEmailDialog(invoice)}
                            title="Email Invoice"
                            data-testid={`email-invoice-${invoice.id}`}
                          >
                            <Mail className="w-4 h-4" />
                          </Button>
                          {["pending_payment", "partial_payment", "overdue"].includes(invoice.status) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openReminderDialog(invoice)}
                              title={invoice.reminder_count ? `Send another reminder (sent ${invoice.reminder_count}x)` : "Send payment reminder"}
                              className={invoice.status === "overdue" ? "text-red-600 hover:text-red-700" : "text-orange-600 hover:text-orange-700"}
                              data-testid={`remind-invoice-${invoice.id}`}
                            >
                              <BellRing className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(invoice)}
                            data-testid={`edit-invoice-${invoice.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDelete(invoice.id)}
                            data-testid={`delete-invoice-${invoice.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Email Invoice Dialog */}
        <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
          <DialogContent className="sm:max-w-[600px] flex flex-col max-h-[88vh] p-0 gap-0">
            <DialogHeader className="px-6 pt-5 pb-3 border-b shrink-0">
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-4 h-4" /> Email Invoice
              </DialogTitle>
              <DialogDescription>
                Send invoice {emailingInvoice?.invoice_number} to {emailingInvoice?.client_name}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={sendInvoiceEmail} className="flex flex-col min-h-0 flex-1">
              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {/* Invoice summary */}
              <div className="bg-muted/50 p-3 rounded-lg text-sm grid grid-cols-2 gap-1">
                <span className="text-muted-foreground">Invoice:</span><span className="font-medium">{emailingInvoice?.invoice_number}</span>
                <span className="text-muted-foreground">Ex-GST:</span><span className="font-medium">{formatCurrency(emailingInvoice?.amount || 0)}</span>
                <span className="text-muted-foreground">GST:</span><span className="font-medium">{formatCurrency(emailingInvoice?.tax || 0)}</span>
                <span className="text-muted-foreground">Total (inc GST):</span><span className="font-medium">{formatCurrency(emailingInvoice?.total || 0)}</span>
              </div>

              {/* Editable Due Date (drives PDF + email body + is persisted) */}
              <div className="space-y-2 p-3 border border-red-200 bg-red-50/40 rounded-lg">
                <Label htmlFor="email_due_date" className="flex items-center gap-1.5 text-red-700">
                  <CalendarDays className="w-3.5 h-3.5" /> Due Date on PDF
                </Label>
                <Input
                  id="email_due_date"
                  type="date"
                  value={emailFormData.due_date || ""}
                  onChange={(e) => setEmailFormData({ ...emailFormData, due_date: e.target.value })}
                  className="bg-white"
                  data-testid="email-due-date-input"
                />
                <p className="text-xs text-muted-foreground">
                  This is the due date that will appear on the PDF and in the email body. It will also be saved to the invoice.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="recipient_email">Recipient Email *</Label>
                <Input id="recipient_email" type="email" value={emailFormData.recipient_email}
                  onChange={(e) => setEmailFormData({ ...emailFormData, recipient_email: e.target.value })}
                  placeholder="client@example.com" required data-testid="email-recipient-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email_cc">CC <span className="text-xs text-muted-foreground">(optional, comma-separated)</span></Label>
                <Input id="email_cc" type="text" value={emailFormData.cc}
                  onChange={(e) => setEmailFormData({ ...emailFormData, cc: e.target.value })}
                  placeholder="cc1@example.com, cc2@example.com" data-testid="email-cc-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email_subject">Subject</Label>
                <Input id="email_subject" value={emailFormData.subject}
                  onChange={(e) => setEmailFormData({ ...emailFormData, subject: e.target.value })}
                  placeholder="Invoice from Malla & Co" data-testid="email-subject-input" />
              </div>
              <div className="space-y-2 p-3 bg-muted/40 border rounded-lg">
                <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Write with AI</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder='e.g. "Polite payment reminder mentioning the due date"'
                    value={aiPrompt}
                    onChange={e => setAiPrompt(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && generateEmailWithAI()}
                    className="text-sm"
                    data-testid="ai-prompt-input"
                  />
                  <Button type="button" variant="secondary" size="sm" onClick={generateEmailWithAI}
                    disabled={generatingEmail || !aiPrompt.trim()} className="shrink-0" data-testid="ai-generate-btn">
                    {generatingEmail ? "Writing..." : "Generate"}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">Generates the body paragraph — greeting &amp; signature are added automatically.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email_message">Message (appears in email body)</Label>
                <Textarea id="email_message" value={emailFormData.message}
                  onChange={(e) => setEmailFormData({ ...emailFormData, message: e.target.value })}
                  placeholder="e.g. Please review and let us know if you have any questions."
                  rows={2} data-testid="email-message-input" />
              </div>

              {/* Timesheet Section */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeTimesheet}
                      onChange={e => setIncludeTimesheet(e.target.checked)}
                      data-testid="include-timesheet-checkbox"
                      className="rounded border-input"
                    />
                    <span className="flex items-center gap-1 text-sm font-medium"><Clock className="w-3.5 h-3.5" /> Include Timesheet</span>
                  </label>
                  {includeTimesheet && (
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => { const p = shiftWeek(timesheetWeekStart, -1); setTimesheetWeekStart(p); fetchTimesheetForWeek(p, emailingInvoice?.client_id); }} className="p-1 rounded hover:bg-accent" title="Previous week"><ChevronLeft className="w-4 h-4" /></button>
                      <span className="text-xs text-muted-foreground min-w-[140px] text-center">{formatWeekLabel(timesheetWeekStart) || "No week"}</span>
                      <button type="button" onClick={() => { const n = shiftWeek(timesheetWeekStart, 1); setTimesheetWeekStart(n); fetchTimesheetForWeek(n, emailingInvoice?.client_id); }} className="p-1 rounded hover:bg-accent" title="Next week"><ChevronRight className="w-4 h-4" /></button>
                    </div>
                  )}
                </div>

                {includeTimesheet && (
                  <>
                    {loadingTimesheet && <p className="text-xs text-muted-foreground animate-pulse py-2 text-center">Loading timesheet...</p>}

                    {!loadingTimesheet && timesheetRows.length === 0 && (
                      <p className="text-xs text-muted-foreground py-2 text-center">No timesheet entries for this client this week.</p>
                    )}

                    {!loadingTimesheet && timesheetRows.length > 0 && (() => {
                      // Build horizontal pivot: projects × dates
                      const dates = [...new Set(timesheetRows.map(r => r.date))].sort();
                      const projects = [...new Map(timesheetRows.map(r => [r.project_name, r])).keys()];
                      const lookup = {};
                      timesheetRows.forEach(r => {
                        lookup[r.project_name] = lookup[r.project_name] || {};
                        lookup[r.project_name][r.date] = (lookup[r.project_name][r.date] || 0) + r.hours;
                      });
                      const fmtDay = d => { try { const dt = new Date(d + "T00:00:00"); return dt.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }); } catch { return d; } };
                      const dayTotals = {};
                      dates.forEach(d => { dayTotals[d] = timesheetRows.filter(r => r.date === d).reduce((s, r) => s + r.hours, 0); });
                      const grandTotal = timesheetRows.reduce((s, r) => s + r.hours, 0);
                      return (
                        <div className="border rounded-lg overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead className="bg-muted/50">
                              <tr>
                                <th className="text-left p-2 font-medium text-muted-foreground whitespace-nowrap">Project</th>
                                {dates.map(d => <th key={d} className="text-center p-2 font-medium text-muted-foreground whitespace-nowrap">{fmtDay(d)}</th>)}
                                <th className="text-center p-2 font-medium text-muted-foreground">Total</th>
                                <th className="w-6"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {projects.map(p => {
                                const rowTotal = dates.reduce((s, d) => s + (lookup[p]?.[d] || 0), 0);
                                return (
                                  <tr key={p} className="border-t hover:bg-accent/20 transition-colors">
                                    <td className="p-2 font-medium whitespace-nowrap">{p}</td>
                                    {dates.map(d => <td key={d} className="p-2 text-center text-muted-foreground">{lookup[p]?.[d] > 0 ? lookup[p][d].toFixed(1) : <span className="opacity-30">—</span>}</td>)}
                                    <td className="p-2 text-center font-semibold text-primary">{rowTotal.toFixed(1)}</td>
                                    <td className="p-2">
                                      <button type="button" onClick={() => setTimesheetRows(rows => rows.filter(r => r.project_name !== p))} className="text-destructive hover:text-destructive/80" title="Remove project from email" data-testid={`remove-ts-project-${p}`}>
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                            <tfoot>
                              <tr className="border-t bg-muted/30">
                                <td className="p-2 font-semibold text-foreground">Total</td>
                                {dates.map(d => <td key={d} className="p-2 text-center font-medium">{dayTotals[d] > 0 ? dayTotals[d].toFixed(1) : ""}</td>)}
                                <td className="p-2 text-center font-bold text-primary" data-testid="timesheet-total-hours">{grandTotal.toFixed(1)} hrs</td>
                                <td></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>

              {/* Custom Table Builder */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Custom Table (optional)</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addTableRow} data-testid="add-table-row-btn">
                    <PlusCircle className="w-3 h-3 mr-1" /> Add Row
                  </Button>
                </div>
                {emailFormData.table_rows.length > 0 && (
                  <div className="space-y-2 border rounded-lg p-3 bg-muted/30">
                    {emailFormData.table_rows.map((row, idx) => (
                      <div key={row._key ?? `tr-${idx}`} className="flex gap-2 items-center">
                        <Input placeholder="Label" value={row.label} onChange={e => updateTableRow(idx, 'label', e.target.value)} className="flex-1" data-testid={`table-row-label-${idx}`} />
                        <Input placeholder="Value" value={row.value} onChange={e => updateTableRow(idx, 'value', e.target.value)} className="flex-1" data-testid={`table-row-value-${idx}`} />
                        <Button type="button" variant="ghost" size="sm" className="text-destructive px-2" onClick={() => removeTableRow(idx)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <p className="text-xs text-muted-foreground">These rows will appear as a table in the email body.</p>
                  </div>
                )}
              </div>

              {/* Signature preview */}
              <div className="bg-muted/40 p-3 rounded-lg text-xs text-muted-foreground border-l-2 border-primary/30">
                <p className="font-medium text-foreground mb-1">Signature (auto-added)</p>
                <p>Best Regards,</p>
                <p>Harish Malla</p>
                <p>+61 434 317 893</p>
              </div>
              </div>{/* end scrollable */}
              <div className="shrink-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-6 py-4 border-t bg-background">
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="test-send-label">
                  <input
                    type="checkbox"
                    checked={!!emailFormData.test_send}
                    onChange={(e) => setEmailFormData({ ...emailFormData, test_send: e.target.checked })}
                    className="h-4 w-4 accent-primary"
                    data-testid="test-send-checkbox"
                  />
                  <span>
                    <span className="font-medium">Test send</span>
                    <span className="text-muted-foreground"> — don&apos;t change invoice status</span>
                  </span>
                </label>
                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={sendingEmail} data-testid="send-email-btn">
                    {sendingEmail
                      ? <><Mail className="w-4 h-4 mr-2 animate-pulse" />Sending...</>
                      : emailFormData.test_send
                        ? <><Mail className="w-4 h-4 mr-2" />Send Test</>
                        : <><Mail className="w-4 h-4 mr-2" />Send &amp; Mark Pending</>
                    }
                  </Button>
                </div>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Payment Reminder Dialog */}
        <Dialog open={reminderDialogOpen} onOpenChange={setReminderDialogOpen}>
          <DialogContent className="sm:max-w-[560px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <BellRing className="w-4 h-4 text-orange-600" /> Send Payment Reminder
              </DialogTitle>
              <DialogDescription>
                Professional reminder for {remindingInvoice?.invoice_number} — {remindingInvoice?.client_name}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={sendReminder} className="space-y-4">
              {remindingInvoice && (
                <div className={`p-3 rounded-lg text-sm grid grid-cols-2 gap-1 ${remindingInvoice.status === "overdue" ? "bg-red-50 border border-red-200" : "bg-orange-50 border border-orange-200"}`}>
                  <span className="text-muted-foreground">Amount owed:</span>
                  <span className="font-semibold">{formatCurrency((remindingInvoice.outstanding_balance ?? remindingInvoice.total) || 0)}</span>
                  <span className="text-muted-foreground">Due date:</span>
                  <span className={`font-medium ${remindingInvoice.status === "overdue" ? "text-red-700" : ""}`}>
                    {remindingInvoice.due_date || "—"}
                    {remindingInvoice.status === "overdue" && " (Overdue)"}
                  </span>
                  {(remindingInvoice.reminder_count || 0) > 0 && (
                    <>
                      <span className="text-muted-foreground">Previously reminded:</span>
                      <span className="font-medium">{remindingInvoice.reminder_count}x · last {remindingInvoice.last_reminder_sent_date || "—"}</span>
                    </>
                  )}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="rem_recipient">Recipient Email *</Label>
                <Input id="rem_recipient" type="email" required
                  value={reminderFormData.recipient_email}
                  onChange={(e) => setReminderFormData({ ...reminderFormData, recipient_email: e.target.value })}
                  placeholder="client@example.com"
                  data-testid="reminder-recipient-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rem_cc">CC <span className="text-xs text-muted-foreground">(optional, comma-separated)</span></Label>
                <Input id="rem_cc" type="text"
                  value={reminderFormData.cc}
                  onChange={(e) => setReminderFormData({ ...reminderFormData, cc: e.target.value })}
                  placeholder="cc1@example.com, cc2@example.com"
                  data-testid="reminder-cc-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rem_message">Extra note <span className="text-xs text-muted-foreground">(optional — appears above the standard message)</span></Label>
                <Textarea id="rem_message" rows={2}
                  value={reminderFormData.custom_message}
                  onChange={(e) => setReminderFormData({ ...reminderFormData, custom_message: e.target.value })}
                  placeholder="e.g. Any issues with the invoice, please let me know."
                  data-testid="reminder-message-input" />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input type="checkbox"
                  checked={!!reminderFormData.include_pdf}
                  onChange={(e) => setReminderFormData({ ...reminderFormData, include_pdf: e.target.checked })}
                  className="h-4 w-4 accent-primary"
                  data-testid="reminder-attach-pdf-checkbox" />
                <span>Attach invoice PDF</span>
              </label>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setReminderDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={sendingReminder || !reminderFormData.recipient_email} data-testid="send-reminder-btn">
                  {sendingReminder
                    ? <><BellRing className="w-4 h-4 mr-2 animate-pulse" />Sending...</>
                    : <><BellRing className="w-4 h-4 mr-2" />Send Reminder</>
                  }
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
